import { ChevronLeft, ChevronRight, SkipBack, SkipForward } from "lucide-react";

import { strings } from "@perceptron/constants";
import { Button } from "@perceptron/ui/components/button";

import type { TimelinePlayerBridge } from "../../bridge";
import type { TrackingInspectorRow } from "./status";
import { getFrameSeekTime, getFrameStep } from "./tools";
import { formatSeconds, toPresentationUs } from "../../utils/time";

const copy = strings.trackingInspector;

export function TrackFrameNavigation({
  bridge,
  row,
  currentTime,
  hint,
}: {
  bridge: TimelinePlayerBridge;
  row: TrackingInspectorRow;
  currentTime: number;
  hint: { id: string; text: string };
}) {
  const previous = getFrameStep(row, currentTime, -1);
  const next = getFrameStep(row, currentTime, 1);
  const isCurrentFrame = (time: number | undefined) => {
    if (time === undefined) return false;
    const targetUs = toPresentationUs(time);
    if (!row.frame) return targetUs === toPresentationUs(currentTime);
    return (
      targetUs >= toPresentationUs(row.frame.timestamp) &&
      targetUs < toPresentationUs(row.frame.timestamp + row.frame.duration)
    );
  };
  const atStartGT = isCurrentFrame(row.anchorTimes?.[0]);
  const atEndGT = isCurrentFrame(row.anchorTimes?.[1]);
  const seek = (time: number | undefined) => {
    if (time === undefined) return;
    bridge.pause();
    bridge.seek(getFrameSeekTime(time));
  };

  return (
    <div aria-label={copy.frameNavigation} className="space-y-2" role="group">
      {row.anchorTimes && (
        <dl className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-xs" title={copy.anchors}>
          <dt className="text-foreground-secondary">{copy.anchors}</dt>
          <dd className="tabular-nums">
            {formatSeconds(row.anchorTimes[0])} → {formatSeconds(row.anchorTimes[1])}
          </dd>
        </dl>
      )}
      <div className="grid grid-cols-2 gap-2">
        <Button
          aria-describedby={hint.id}
          className="gap-1.5 px-2 text-xs shadow-none"
          disabled={previous === undefined}
          size="sm"
          type="button"
          variant="outline"
          onClick={() => seek(previous)}
        >
          <ChevronLeft aria-hidden="true" className="size-3.5 shrink-0" />
          {copy.previousFrame}
        </Button>
        <Button
          aria-describedby={hint.id}
          className="gap-1.5 px-2 text-xs shadow-none"
          disabled={next === undefined}
          size="sm"
          type="button"
          variant="outline"
          onClick={() => seek(next)}
        >
          {copy.nextFrame}
          <ChevronRight aria-hidden="true" className="size-3.5 shrink-0" />
        </Button>
        <Button
          aria-describedby={hint.id}
          aria-label={copy.startGT}
          className="gap-1.5 px-2 text-xs shadow-none"
          disabled={!row.anchorTimes || atStartGT}
          size="sm"
          type="button"
          variant="outline"
          onClick={() => seek(row.anchorTimes?.[0])}
        >
          <SkipBack aria-hidden="true" className="size-3.5 shrink-0" />
          {atStartGT ? copy.atStartGT : copy.startGTShort}
        </Button>
        <Button
          aria-describedby={hint.id}
          aria-label={copy.endGT}
          className="gap-1.5 px-2 text-xs shadow-none"
          disabled={!row.anchorTimes || atEndGT}
          size="sm"
          type="button"
          variant="outline"
          onClick={() => seek(row.anchorTimes?.[1])}
        >
          {atEndGT ? copy.atEndGT : copy.endGTShort}
          <SkipForward aria-hidden="true" className="size-3.5 shrink-0" />
        </Button>
      </div>
      {!row.frameTimes?.length && (
        <p className="text-xs leading-relaxed text-foreground-secondary" id={hint.id}>
          {hint.text}
        </p>
      )}
    </div>
  );
}
